import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getCollegeInfo, generateReceiptFooter, fetchLogoAsBase64, fetchReceiptTerms } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';

// Helper function to convert number to words (Indian number system)
const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };
  
  if (num < 1000) return convertLessThanThousand(num);
  if (num < 100000) {
    return convertLessThanThousand(Math.floor(num / 1000)) + ' Thousand' + 
           (num % 1000 ? ' ' + convertLessThanThousand(num % 1000) : '');
  }
  if (num < 10000000) {
    return convertLessThanThousand(Math.floor(num / 100000)) + ' Lakh' + 
           (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
  }
  return convertLessThanThousand(Math.floor(num / 10000000)) + ' Crore' + 
         (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
};

// Common header generator with logo support
const generateHeader = async (doc, collegeInfo, title = 'SEAT BOOKING RECEIPT', logoBase64 = null) => {
  const resolvedFont = await ensureRupeeFont(doc);
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let yPos = 15;

  const collegeName = collegeInfo?.college_name || collegeInfo?.name || 'Institution';
  const collegeAddress = collegeInfo?.address || '';
  const collegeCity = collegeInfo?.city || '';
  const collegeState = collegeInfo?.state || '';
  const collegePincode = collegeInfo?.pincode || collegeInfo?.pin_code || '';
  const collegePhone = collegeInfo?.phone || '';
  const collegeEmail = collegeInfo?.email || '';
  const collegeLogo = logoBase64 || collegeInfo?.logo_base64 || collegeInfo?.logo_url || collegeInfo?.logo || '';

  // Add logo if available
  let logoAdded = false;
  if (collegeLogo) {
    try {
      const logoWidth = 20;
      const logoHeight = 20;
      const logoX = margin;
      
      if (collegeLogo.startsWith('data:image')) {
        doc.addImage(collegeLogo, 'PNG', logoX, yPos - 5, logoWidth, logoHeight);
        logoAdded = true;
      }
    } catch (e) {
      console.log('Could not add logo:', e);
    }
  }

  // Adjust text position based on logo
  const textStartX = logoAdded ? margin + 25 : pageWidth / 2;
  // Calculate max width for text (considering logo space)
  const maxTextWidth = logoAdded ? (pageWidth - margin - textStartX - 5) : (pageWidth - 2 * margin);

  // College Name
  doc.setFontSize(16);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 51, 102);
  
  if (logoAdded) {
    doc.text(collegeName.toUpperCase(), textStartX, yPos + 2);
  } else {
    doc.text(collegeName.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += 6;

  // Full Address with text wrapping for long addresses
  doc.setFontSize(9);
  doc.setFont(resolvedFont, 'normal');
  doc.setTextColor(60, 60, 60);

  if (collegeAddress) {
    const wrappedAddress = doc.splitTextToSize(collegeAddress, maxTextWidth);
    wrappedAddress.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
  }

  const cityStatePincode = [collegeCity, collegeState, collegePincode].filter(Boolean).join(', ');
  if (cityStatePincode) {
    const wrappedCity = doc.splitTextToSize(cityStatePincode, maxTextWidth);
    wrappedCity.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
  }

  // Phone and Email
  const contactParts = [];
  if (collegePhone) contactParts.push(`Phone: ${collegePhone}`);
  if (collegeEmail) contactParts.push(`Email: ${collegeEmail}`);
  
  if (contactParts.length > 0) {
    doc.setFontSize(8);
    const contactText = contactParts.join('   |   ');
    const wrappedContact = doc.splitTextToSize(contactText, maxTextWidth);
    wrappedContact.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
    yPos += 1;
  }

  // Ensure yPos is below logo
  if (logoAdded) {
    yPos = Math.max(yPos, 35);
  }

  // Decorative double line
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(1);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Receipt Title
  doc.setFontSize(14);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(title, pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;

  const titleWidth = doc.getTextWidth(title);
  doc.setLineWidth(0.5);
  doc.line((pageWidth - titleWidth) / 2 - 5, yPos, (pageWidth + titleWidth) / 2 + 5, yPos);
  yPos += 8;

  doc.setTextColor(0, 0, 0);
  return yPos;
};

export const generateBookingReceipt = async (bookingData, collegeInfo = null) => {
  try {
    if (!bookingData) {
      throw new Error('Booking data is required');
    }

    // Get college info
    if (!collegeInfo) {
      collegeInfo = getCollegeInfo() || {};
    }
    
    // Fetch logo as base64 if URL is provided
    let logoBase64 = collegeInfo.logo_base64 || null;
    if (!logoBase64 && (collegeInfo.logo_url || collegeInfo.logo)) {
      logoBase64 = await fetchLogoAsBase64(collegeInfo.logo_url || collegeInfo.logo);
    }
    
    const doc = new jsPDF();
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
    const pageWidth = doc.internal.pageSize.width;
    
    // Generate Header with logo
    let yPos = await generateHeader(doc, collegeInfo, 'SEAT BOOKING RECEIPT', logoBase64);
    
    const bookingId = String(bookingData.id || bookingData._id || '');
    const receiptNo = String(bookingData.receipt_no || bookingData.booking_id || `BKG-${bookingId.substring(0, 8).toUpperCase() || Date.now()}`);
    
    // Receipt Info
    doc.setFontSize(10);
    doc.setFont(resolvedFont, 'normal');
    doc.text(`Receipt No: ${receiptNo}`, 20, yPos);
    
    const bookingDate = bookingData.booking_date || bookingData.created_at;
    const formattedDate = bookingDate ? formatDate(bookingDate) : formatDate(new Date());
    doc.text(`Date: ${formattedDate}`, pageWidth - 60, yPos);
    yPos += 10;
    
    // Applicant Information Section
    doc.setFontSize(11);
    doc.setFont(resolvedFont, 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
    doc.text('APPLICANT INFORMATION', 22, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont(resolvedFont, 'normal');
    
    const applicantName = String(bookingData.applicant_name || bookingData.student_name || bookingData.name || 'N/A');
    const applicantPhone = String(bookingData.phone || bookingData.applicant_phone || 'N/A');
    const applicantEmail = String(bookingData.email || bookingData.applicant_email || 'N/A');
    const fatherName = String(bookingData.father_name || 'N/A');
    
    doc.text(`Name: ${applicantName}`, 20, yPos);
    doc.text(`Phone: ${applicantPhone}`, pageWidth / 2, yPos);
    yPos += 6;
    
    doc.text(`Father's Name: ${fatherName}`, 20, yPos);
    if (applicantEmail !== 'N/A') {
      doc.text(`Email: ${applicantEmail}`, pageWidth / 2, yPos);
    }
    yPos += 10;
    
    // Course Information Section
    doc.setFontSize(11);
    doc.setFont(resolvedFont, 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
    doc.text('COURSE DETAILS', 22, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont(resolvedFont, 'normal');
    
    const courseName = String(bookingData.course_name || bookingData.course || 'N/A');
    const session = String(bookingData.session || bookingData.session_name || 'N/A');
    const entryType = String(bookingData.entry_type || 'Regular');
    const courseFee = parseFloat(bookingData.course_fee || 0);
    
    doc.text(`Course: ${courseName}`, 20, yPos);
    doc.text(`Entry Type: ${entryType}`, pageWidth / 2, yPos);
    yPos += 6;
    doc.text(`Session: ${session}`, 20, yPos);
    if (courseFee > 0) {
      // Switch to Unicode font for the ₹-bearing line so the symbol renders
      // properly. Switch back to helvetica afterward to keep section visually
      // consistent with other plain-text rows.
      doc.setFont(resolvedFont, 'normal');
      doc.text(`Course Fee: ${CURRENCY} ${formatINR(courseFee)}/-`, pageWidth / 2, yPos);
      doc.setFont(resolvedFont, 'normal');
    }
    yPos += 10;
    
    // Payment Details Section
    doc.setFontSize(11);
    doc.setFont(resolvedFont, 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
    doc.text('PAYMENT DETAILS', 22, yPos);
    yPos += 8;
    
    const bookingAmount = parseFloat(bookingData.booking_amount || bookingData.amount || 0);
    // ── Derive payment mode display ───────────────────────────────────
    // The seat-booking form uses a `payment_modes` array (multi-mode split
    // like ₹5000 UPI + ₹5000 Cash). The top-level `payment_mode` string is
    // often left empty in that flow, so blindly defaulting to 'Cash' here
    // was showing 'Cash' on the receipt for UPI/Bank/Card bookings even
    // though every other page (ledger, income report, cash/bank register)
    // read the correct mode from `payment_modes[0].mode`. Derive it here.
    let paymentMode = String(bookingData.payment_mode || '').trim();
    if (!paymentMode && Array.isArray(bookingData.payment_modes) && bookingData.payment_modes.length) {
      const modes = bookingData.payment_modes
        .filter(p => p && (p.mode || p.payment_mode))
        .map(p => String(p.mode || p.payment_mode));
      if (modes.length === 1) {
        paymentMode = modes[0];
      } else if (modes.length > 1) {
        // Multi-split: e.g. "UPI + Cash"
        paymentMode = modes.join(' + ');
      }
    }
    if (!paymentMode) paymentMode = 'Cash';
    const transactionNo = bookingData.transaction_number || bookingData.transaction_id || '';
    
    doc.setFontSize(10);
    doc.setFont(resolvedFont, 'normal');
    doc.text(`Booking Amount:`, 20, yPos);
    doc.setFont(resolvedFont, 'bold');
    doc.setFontSize(12);
    doc.text(`${CURRENCY} ${formatINR(bookingAmount)}/-`, 55, yPos);
    yPos += 6;
    
    doc.setFontSize(10);
    doc.setFont(resolvedFont, 'normal');
    doc.text(`Amount in Words: ${numberToWords(Math.round(bookingAmount))} Rupees Only`, 20, yPos);
    yPos += 6;
    
    doc.text(`Payment Mode: ${paymentMode}`, 20, yPos);
    if (transactionNo) {
      doc.text(`Transaction No: ${transactionNo}`, pageWidth / 2, yPos);
    }
    yPos += 10;
    
    // Important Note
    doc.setFillColor(255, 250, 230);
    doc.rect(20, yPos, pageWidth - 40, 25, 'F');
    doc.setDrawColor(255, 200, 0);
    doc.setLineWidth(0.5);
    doc.rect(20, yPos, pageWidth - 40, 25);
    
    doc.setFontSize(9);
    doc.setFont(resolvedFont, 'bold');
    doc.setTextColor(150, 100, 0);
    doc.text('IMPORTANT:', 25, yPos + 6);
    doc.setFont(resolvedFont, 'normal');
    doc.setFontSize(8);
    doc.text('1. This receipt confirms your seat booking. Please keep it safe.', 25, yPos + 12);
    doc.text('2. Complete your admission within the stipulated time to confirm your seat.', 25, yPos + 17);
    doc.text('3. Booking amount will be adjusted against your total course fee.', 25, yPos + 22);
    
    doc.setTextColor(0, 0, 0);
    
    // Footer with tenant-specific terms (overrides the hardcoded list above)
    const terms = await fetchReceiptTerms('booking');
    generateReceiptFooter(doc, collegeInfo, terms);
    
    // Save PDF
    const fileName = `BookingReceipt_${receiptNo}_${Date.now()}.pdf`;
    doc.save(fileName);
    return doc;
    
  } catch (error) {
    console.error('Error generating booking receipt:', error);
    throw error;
  }
};

export default generateBookingReceipt;
